import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  registerVolunteerRole,
  getVolunteerRoles,
  updateVolunteerAvailability,
  getVolunteerCareGroups,
  getMentorPairs,
  logMentoringMilestone,
  getPeerConnectionsByPeer,
  sendPeerMessage,
  getPeerMessages,
  flagConcern,
  addVolunteerAvailabilitySlot,
  getVolunteerAvailabilitySlots,
  removeVolunteerAvailabilitySlot,
  getVolunteerAppointments,
  respondToAppointment,
  cancelAppointment,
  sendAppointmentMessage,
  getAppointmentMessages,
  getNotifications,
  markNotificationRead,
} from '../../../api/programmesApi';

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt12 = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
};

const isMessagingOpen = (appt) =>
  appt.status === 'confirmed' && Date.now() >= new Date(`${appt.date}T${appt.time}`).getTime();

const APPT_STATUS = {
  pending_approval: { cls: 'bb-badge--yellow', label: 'Awaiting Your Response' },
  confirmed:        { cls: 'bb-badge--green',  label: 'Confirmed' },
  declined:         { cls: 'bb-badge--gray',   label: 'Declined' },
  cancelled:        { cls: 'bb-badge--gray',   label: 'Cancelled' },
  completed:        { cls: 'bb-badge--gray',   label: 'Completed' },
};

const ROLE_TYPES = [
  {
    key: 'Facilitator',
    label: 'Facilitator',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
      </svg>
    ),
    description: 'Lead Care & Connect peer support groups. Facilitate structured sessions to help members process grief and build resilience in a safe group environment.',
  },
  {
    key: 'Mentor',
    label: 'Mentor',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ),
    description: 'Provide one-on-one guidance through the Rebuild & Shine mentoring programme. Share your experience and help a member navigate life after loss.',
  },
  {
    key: 'Befriender',
    label: 'Befriender',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 11V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2"/>
        <path d="M14 10V4a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2"/>
        <path d="M10 10.5V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v8"/>
        <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/>
      </svg>
    ),
    description: 'Offer peer support through the Widow Lifeline programme. Walk alongside a member as a compassionate peer who understands their journey.',
  },
  {
    key: 'Volunteer',
    label: 'Volunteer',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    description: 'Assist with general volunteer duties such as event support, outreach, and community activities.',
  },
];

// ── In-App Appointment Chat ────────────────────────────────────────────────────
function AppointmentChat({ orgId, appointmentId, volunteerId, volunteerName, memberFirstName }) {
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState('');
  const [sending, setSending]   = useState(false);
  const bottomRef               = useRef(null);

  const loadMessages = useCallback(() => {
    getAppointmentMessages(orgId, appointmentId).then(setMessages).catch(() => {});
  }, [orgId, appointmentId]);

  useEffect(() => {
    loadMessages();
    const iv = setInterval(loadMessages, 8000);
    return () => clearInterval(iv);
  }, [loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await sendAppointmentMessage(orgId, appointmentId, volunteerId, volunteerName, 'volunteer', text.trim());
      setText('');
      loadMessages();
    } catch { /* silent */ } finally { setSending(false); }
  };

  return (
    <div style={{ marginTop: 12, border: '1px solid #C7D2FE', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ background: '#4338CA', color: '#fff', padding: '8px 14px', fontSize: 12, fontWeight: 700 }}>
        💬 Secure Chat — with {memberFirstName}
        <span style={{ fontWeight: 400, marginLeft: 8, opacity: 0.8 }}>No contact info shared</span>
      </div>
      <div style={{ height: 220, overflowY: 'auto', padding: '10px 14px', background: '#F9FAFB' }}>
        {messages.length === 0 && (
          <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', marginTop: 20 }}>
            No messages yet. Say hello!
          </div>
        )}
        {messages.map((m) => {
          const mine = m.senderId === volunteerId;
          return (
            <div key={m.id} style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
              <div style={{
                display: 'inline-block',
                background: mine ? '#4338CA' : '#E5E7EB',
                color: mine ? '#fff' : '#1F2937',
                padding: '7px 12px', borderRadius: 12,
                maxWidth: '75%', fontSize: 13, lineHeight: 1.4,
              }}>
                {m.text}
              </div>
              <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                {mine ? 'You' : memberFirstName} ·{' '}
                {m.createdAt?.toDate ? m.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '8px 10px', borderTop: '1px solid #E5E7EB', background: '#fff' }}>
        <input
          className="bb-input"
          style={{ flex: 1, fontSize: 13 }}
          placeholder="Type a message…"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
        />
        <button
          className="bb-btn-primary"
          style={{ background: '#4338CA', fontSize: 12, padding: '6px 14px' }}
          onClick={handleSend}
          disabled={sending || !text.trim()}
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

// ── Peer Chat (Widow-to-Widow) ─────────────────────────────────────────────────
function PeerChat({ orgId, connectionId, volunteerId, volunteerName, memberFirstName }) {
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState('');
  const [sending, setSending]   = useState(false);
  const bottomRef               = useRef(null);

  const loadMessages = useCallback(() => {
    getPeerMessages(orgId, connectionId).then(setMessages).catch(() => {});
  }, [orgId, connectionId]);

  useEffect(() => {
    loadMessages();
    const iv = setInterval(loadMessages, 10000);
    return () => clearInterval(iv);
  }, [loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await sendPeerMessage(orgId, connectionId, volunteerId, volunteerName, 'volunteer', text.trim());
      setText('');
      loadMessages();
    } catch { } finally { setSending(false); }
  };

  return (
    <div style={{ marginTop: 12, border: '1px solid #FED7AA', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ background: '#F97316', color: '#fff', padding: '8px 14px', fontSize: 12, fontWeight: 700 }}>
        💬 Chat with {memberFirstName}
        <span style={{ fontWeight: 400, marginLeft: 8, opacity: 0.8 }}>No contact info shared</span>
      </div>
      <div style={{ height: 200, overflowY: 'auto', padding: '10px 14px', background: '#FFF7ED' }}>
        {messages.length === 0 && (
          <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', marginTop: 20 }}>
            No messages yet. Reach out to {memberFirstName}!
          </div>
        )}
        {messages.map(m => {
          const mine = m.senderId === volunteerId;
          return (
            <div key={m.id} style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
              <div style={{ background: mine ? '#F97316' : '#E5E7EB', color: mine ? '#fff' : '#1F2937', padding: '7px 12px', borderRadius: 12, maxWidth: '75%', fontSize: 13, lineHeight: 1.4 }}>
                {m.text}
              </div>
              <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                {mine ? 'You' : memberFirstName} ·{' '}
                {m.createdAt?.toDate ? m.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '8px 10px', borderTop: '1px solid #FED7AA', background: '#fff' }}>
        <input
          className="bb-input"
          style={{ flex: 1, fontSize: 13 }}
          placeholder="Type a message…"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
        />
        <button
          className="bb-btn-primary"
          style={{ background: '#F97316', fontSize: 12, padding: '6px 14px' }}
          onClick={handleSend}
          disabled={sending || !text.trim()}
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

// ── Availability Manager ───────────────────────────────────────────────────────
function AvailabilityManager({ orgId, volunteerId, volunteerName }) {
  const [slots, setSlots]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]     = useState({ date: '', time: '', duration: '60' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const today = new Date().toISOString().split('T')[0];

  const load = () => {
    setLoading(true);
    getVolunteerAvailabilitySlots(orgId, volunteerId)
      .then(setSlots).catch(() => setSlots([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [orgId, volunteerId]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      await addVolunteerAvailabilitySlot(orgId, volunteerId, volunteerName, form.date, form.time, parseInt(form.duration, 10));
      setMsg('Slot added. Members can now book this time.');
      setShowForm(false);
      setForm({ date: '', time: '', duration: '60' });
      load();
    } catch (err) {
      setMsg(err.message || 'Error adding slot.');
    } finally { setSaving(false); }
  };

  const handleRemove = async (slot) => {
    if (!window.confirm('Remove this time slot?')) return;
    try {
      await removeVolunteerAvailabilitySlot(orgId, slot.id);
      setMsg('Slot removed.');
      load();
    } catch { setMsg('Error removing slot.'); }
  };

  const upcoming = slots.filter(s => new Date(`${s.date}T${s.time}`).getTime() > Date.now());
  const past     = slots.filter(s => new Date(`${s.date}T${s.time}`).getTime() <= Date.now());

  return (
    <div style={{ marginTop: 28, borderTop: '1px solid #E5E7EB', paddingTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1F2937', margin: 0 }}>My Availability</h3>
        <button
          className="bb-btn-outline-sm"
          style={{ borderColor: '#4338CA', color: '#4338CA' }}
          onClick={() => { setShowForm(f => !f); setMsg(''); }}
        >
          {showForm ? 'Cancel' : '+ Add Time Slot'}
        </button>
      </div>

      {msg && <div className="bb-alert bb-alert--info" style={{ marginBottom: 10, fontSize: 13 }}>{msg}</div>}

      {showForm && (
        <form onSubmit={handleAdd} style={{ background: '#EEF2FF', borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
            <div className="bb-form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: 12 }}>Date <span style={{ color: '#DC2626' }}>*</span></label>
              <input
                type="date"
                className="bb-input"
                min={today}
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                required
              />
            </div>
            <div className="bb-form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: 12 }}>Time <span style={{ color: '#DC2626' }}>*</span></label>
              <input
                type="time"
                className="bb-input"
                value={form.time}
                onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                required
              />
            </div>
            <div className="bb-form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: 12 }}>Duration</label>
              <select
                className="bb-select"
                value={form.duration}
                onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}
              >
                <option value="30">30 min</option>
                <option value="60">60 min</option>
              </select>
            </div>
            <button type="submit" className="bb-btn-primary" style={{ background: '#4338CA', padding: '8px 14px', fontSize: 12 }} disabled={saving}>
              {saving ? '…' : 'Add'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="bb-loading-row"><div className="bb-loading-spinner" /><span>Loading slots…</span></div>
      ) : upcoming.length === 0 && past.length === 0 ? (
        <div className="bb-empty-state" style={{ fontSize: 13 }}>
          No time slots yet. Add your first slot so members can book an appointment with you.
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <table className="bb-table" style={{ marginBottom: 16 }}>
              <thead>
                <tr>
                  <th>Date</th><th>Time</th><th>Duration</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {upcoming.map(s => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 500 }}>{fmtDate(s.date)}</td>
                    <td>{fmt12(s.time)}</td>
                    <td>{s.duration} min</td>
                    <td>
                      <span className={`bb-badge ${s.status === 'booked' ? 'bb-badge--blue' : 'bb-badge--green'}`}>
                        {s.status === 'booked' ? 'Booked' : 'Open'}
                      </span>
                    </td>
                    <td>
                      {s.status === 'available' && (
                        <button className="bb-btn-danger-sm" style={{ fontSize: 11 }} onClick={() => handleRemove(s)}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {past.length > 0 && (
            <details style={{ fontSize: 12, color: '#6B7280' }}>
              <summary style={{ cursor: 'pointer', marginBottom: 6 }}>Past slots ({past.length})</summary>
              <table className="bb-table">
                <thead><tr><th>Date</th><th>Time</th><th>Duration</th><th>Status</th></tr></thead>
                <tbody>
                  {past.map(s => (
                    <tr key={s.id} style={{ opacity: 0.6 }}>
                      <td>{fmtDate(s.date)}</td>
                      <td>{fmt12(s.time)}</td>
                      <td>{s.duration} min</td>
                      <td><span className="bb-badge bb-badge--gray">{s.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </>
      )}
    </div>
  );
}

// ── Flag a Concern ─────────────────────────────────────────────────────────────
function FlagConcernPanel({ orgId, volunteerId, volunteerName }) {
  const [open, setOpen]           = useState(false);
  const [form, setForm]           = useState({ concernType: '', description: '', subjectName: '' });
  const [saving, setSaving]       = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]         = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await flagConcern(orgId, volunteerId, volunteerName, 'volunteer', form.concernType, form.description, '', form.subjectName);
      setSubmitted(true);
      setForm({ concernType: '', description: '', subjectName: '' });
    } catch (err) {
      setError(err.message || 'Failed to submit concern.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop: 24, borderTop: '1px solid #E5E7EB', paddingTop: 16 }}>
      {!open ? (
        <button className="bb-btn-danger-sm" onClick={() => { setOpen(true); setSubmitted(false); }}>
          🚩 Flag a Concern
        </button>
      ) : (
        <div className="bb-vol-section" style={{ borderLeft: '3px solid #EF4444', paddingLeft: 16 }}>
          <h4 style={{ color: '#DC2626', marginBottom: 12 }}>Flag a Concern</h4>
          {submitted ? (
            <div className="bb-alert bb-alert--info">
              Your concern has been submitted to admin. Thank you for keeping our community safe.
              <button className="bb-btn-outline-sm" style={{ marginLeft: 12 }} onClick={() => setOpen(false)}>Close</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && <div className="bb-alert bb-alert--error" style={{ marginBottom: 10 }}>{error}</div>}
              <div className="bb-form-group">
                <label>Concern Type <span style={{ color: '#DC2626' }}>*</span></label>
                <select className="bb-select" value={form.concernType} onChange={e => setForm(f => ({ ...f, concernType: e.target.value }))} required>
                  <option value="">— select type —</option>
                  <option value="safety">Safety</option>
                  <option value="inappropriate_behaviour">Inappropriate Behaviour</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="bb-form-group">
                <label>Member Involved</label>
                <input className="bb-input" placeholder="Member name (if applicable)" value={form.subjectName} onChange={e => setForm(f => ({ ...f, subjectName: e.target.value }))} />
              </div>
              <div className="bb-form-group">
                <label>Description <span style={{ color: '#DC2626' }}>*</span></label>
                <textarea className="bb-textarea" rows={4} placeholder="Describe the concern clearly and factually…" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="bb-btn-danger-sm" disabled={saving}>{saving ? 'Submitting…' : 'Submit Concern'}</button>
                <button type="button" className="bb-btn-outline-sm" onClick={() => setOpen(false)}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab: View Available Roles ──────────────────────────────────────────────────
function ViewAvailableRoles({ orgId, volunteerId, volunteerName }) {
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(null);
  const [regForm, setRegForm] = useState({ bio: '', experience: '', availability: 'active' });
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');

  const load = () => {
    setLoading(true);
    getVolunteerRoles(orgId, volunteerId)
      .then(data => setRoles(data)).catch(() => setRoles([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [orgId, volunteerId]); // eslint-disable-line

  const alreadyRegistered = (type) => roles.some(r => r.type === type);

  const handleRegister = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await registerVolunteerRole(orgId, volunteerId, volunteerName, registering, regForm.bio, regForm.experience, regForm.availability);
      const needsApproval = ['Facilitator', 'Mentor', 'Befriender'].includes(registering);
      setMsg(needsApproval
        ? `Your application to be a ${registering} has been submitted. You will be notified once an admin reviews it.`
        : `You are now registered as ${registering}. Your role is active.`
      );
      setRegistering(null);
      setRegForm({ bio: '', experience: '', availability: 'active' });
      load();
    } catch (err) {
      setMsg(err.message || 'Error registering role.');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="bb-vol-section">
      <div className="bb-loading-row"><div className="bb-loading-spinner" /><span>Loading…</span></div>
    </div>
  );

  return (
    <div className="bb-vol-section">
      {msg && <div className="bb-alert bb-alert--info" style={{ marginBottom: 12 }}>{msg}</div>}

      {/* Register for new roles */}
      <div>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1F2937', marginBottom: 12 }}>Register for a Role</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 20 }}>
          {ROLE_TYPES.map(rt => (
            <div key={rt.key} className="bb-card" style={{ padding: 22 }}>
              <div style={{ marginBottom: 10, color: '#0F766E', lineHeight: 1 }}>{rt.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#1F2937', marginBottom: 4 }}>{rt.label}</div>
              <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.5, marginBottom: 12 }}>{rt.description}</p>
              {alreadyRegistered(rt.key) ? (
                <span className="bb-badge bb-badge--gray" style={{ fontSize: 11 }}>Already registered</span>
              ) : (
                <button
                  className="bb-btn-outline-sm"
                  style={{ borderColor: '#4338CA', color: '#4338CA' }}
                  onClick={() => { setRegistering(rt.key); setRegForm({ bio: '', experience: '', availability: 'active' }); }}
                >
                  Register
                </button>
              )}
            </div>
          ))}
        </div>

        {registering && (
          <div className="bb-vol-section" style={{ borderLeft: '3px solid #4338CA', paddingLeft: 16 }}>
            <h4 style={{ marginBottom: 12, color: '#4338CA' }}>
              Register as {ROLE_TYPES.find(r => r.key === registering)?.label}
            </h4>
            <form onSubmit={handleRegister}>
              <div className="bb-form-group">
                <label>Bio / Why you want to volunteer in this role <span style={{ color: '#DC2626' }}>*</span></label>
                <textarea className="bb-textarea" rows={3} value={regForm.bio} onChange={e => setRegForm(f => ({ ...f, bio: e.target.value }))} placeholder="Tell us a little about yourself and why you're applying for this role…" required />
              </div>
              <div className="bb-form-group">
                <label>Experience Summary</label>
                <textarea className="bb-textarea" rows={2} value={regForm.experience} onChange={e => setRegForm(f => ({ ...f, experience: e.target.value }))} placeholder="Relevant experience, qualifications, or background…" />
              </div>
              <div className="bb-form-group">
                <label>Initial Availability</label>
                <select className="bb-select" value={regForm.availability} onChange={e => setRegForm(f => ({ ...f, availability: e.target.value }))}>
                  <option value="active">Active — available now</option>
                  <option value="unavailable">Unavailable — not ready yet</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="bb-btn-primary" style={{ background: '#4338CA' }} disabled={saving}>
                  {saving ? 'Submitting…' : 'Submit Registration'}
                </button>
                <button type="button" className="bb-btn-outline-sm" onClick={() => setRegistering(null)}>Cancel</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab: My Roles, Groups & Pairs ─────────────────────────────────────────────
function MyRolesGroupsAndPairs({ orgId, volunteerId, volunteerName }) {
  // ── Roles (displayed at top) ──────────────────────────────────────────────
  const [roles, setRoles]         = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [savingAvail, setSavingAvail]   = useState(false);
  const [roleMsg, setRoleMsg]           = useState('');

  const loadRoles = useCallback(() => {
    setRolesLoading(true);
    getVolunteerRoles(orgId, volunteerId)
      .then(data => setRoles(data)).catch(() => setRoles([]))
      .finally(() => setRolesLoading(false));
  }, [orgId, volunteerId]);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const handleToggleAvailability = async (role) => {
    const next = role.availability === 'active' ? 'unavailable' : 'active';
    setSavingAvail(true);
    try {
      await updateVolunteerAvailability(orgId, role.id, next);
      setRoleMsg('Availability updated.');
      loadRoles();
    } catch { setRoleMsg('Error updating availability.'); }
    finally { setSavingAvail(false); }
  };

  const approvedTypes = roles.filter(r => r.status === 'approved').map(r => r.type);

  // ── Groups & Pairs (loaded after roles resolve) ───────────────────────────
  const [groups, setGroups]       = useState([]);
  const [pairs, setPairs]         = useState([]);
  const [peerConns, setPeerConns] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [milestoneText, setMilestoneText] = useState({});
  const [savingMile, setSavingMile]       = useState({});
  const [mileSaved, setMileSaved]         = useState({});
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (rolesLoading) return; // wait for roles before deciding what to fetch
    const fetches = [];
    if (approvedTypes.includes('Facilitator')) fetches.push(getVolunteerCareGroups(orgId, volunteerId).then(d => setGroups(d)).catch(() => {}));
    if (approvedTypes.includes('Mentor'))      fetches.push(getMentorPairs(orgId, volunteerId).then(d => setPairs(d)).catch(() => {}));
    if (approvedTypes.includes('Befriender'))  fetches.push(getPeerConnectionsByPeer(orgId, volunteerId).then(d => setPeerConns(d)).catch(() => {}));
    Promise.all(fetches).finally(() => setLoading(false));
  }, [rolesLoading, orgId, volunteerId]); // eslint-disable-line

  const handleLogMilestone = async (pairId) => {
    const note = milestoneText[pairId];
    if (!note?.trim()) return;
    setSavingMile(s => ({ ...s, [pairId]: true }));
    try {
      await logMentoringMilestone(orgId, pairId, note.trim(), volunteerId);
      setMileSaved(s => ({ ...s, [pairId]: true }));
      setMilestoneText(t => ({ ...t, [pairId]: '' }));
    } catch { setMsg('Error logging milestone.'); }
    finally { setSavingMile(s => ({ ...s, [pairId]: false })); }
  };

  const hasApprovedRole = roles.some(r => r.status === 'approved');

  if (rolesLoading) return (
    <div className="bb-vol-section">
      <div className="bb-loading-row"><div className="bb-loading-spinner" /><span>Loading…</span></div>
    </div>
  );

  const ROLE_COLORS = { Facilitator: '#0D9488', Mentor: '#4338CA', Befriender: '#F97316', Volunteer: '#3A7D44' };

  const fmtMilestoneDate = (d) => {
    if (!d) return '—';
    const date = d?.toDate ? d.toDate() : new Date(d);
    if (isNaN(date)) return '—';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="bb-vol-section">

      {/* ── My Availability ── */}
      {hasApprovedRole && (
        <AvailabilityManager orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} />
      )}

      {/* ── My Registered Roles ── */}
      <div style={{ marginBottom: 32, marginTop: hasApprovedRole ? 28 : 0 }}>
        {roleMsg && <div className="bb-alert bb-alert--info" style={{ marginBottom: 10 }}>{roleMsg}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ width: 4, height: 20, background: '#3A7D44', borderRadius: 2 }} />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1F2937', margin: 0 }}>My Registered Roles</h3>
        </div>
        {roles.length === 0 ? (
          <div className="bb-empty-state">
            You have not registered for any volunteer roles yet. Use the <strong>View Available Roles</strong> tab to apply.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {roles.map(role => (
              <div key={role.id} className="bb-member-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ background: ROLE_COLORS[role.type] || '#6B7280', height: 4 }} />
                <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: '#1F2937' }}>{role.type}</span>
                      <span className={`bb-member-badge ${role.status === 'approved' ? 'bb-member-badge--green' : 'bb-member-badge--amber'}`}>
                        {role.status === 'approved' ? 'Approved' : 'Pending Approval'}
                      </span>
                    </div>
                    {role.bio && <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 4px' }}>{role.bio}</p>}
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                      Availability:{' '}
                      <strong style={{ color: (role.availability || 'active') === 'active' ? '#059669' : '#D97706' }}>
                        {(role.availability || 'active') === 'active' ? 'Active' : 'Unavailable'}
                      </strong>
                    </div>
                  </div>
                  {role.status === 'approved' && (
                    <button
                      className="bb-btn-outline-sm"
                      style={{ fontSize: 11, flexShrink: 0, marginLeft: 12 }}
                      disabled={savingAvail}
                      onClick={() => handleToggleAvailability(role)}
                    >
                      {role.availability === 'active' ? 'Set Unavailable' : 'Set Available'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Groups & Pairs ── */}
      {approvedTypes.length === 0 ? (
        <div className="bb-empty-state">
          You do not have any approved roles yet. Register for a role in the "View Available Roles" tab and await admin approval.
        </div>
      ) : loading ? (
        <div className="bb-loading-row"><div className="bb-loading-spinner" /><span>Loading your groups and pairs…</span></div>
      ) : (
        <>
          {msg && <div className="bb-alert bb-alert--info" style={{ marginBottom: 12 }}>{msg}</div>}

          {/* ── Facilitator: Care & Connect Groups ── */}
          {approvedTypes.includes('Facilitator') && (
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #CCFBF1' }}>
                <div style={{ width: 4, height: 22, background: '#0D9488', borderRadius: 2 }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937' }}>🧭 As Facilitator</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>Care & Connect Groups</div>
                </div>
              </div>
              {groups.length === 0 ? (
                <div className="bb-empty-state" style={{ fontSize: 13 }}>No groups assigned to you yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {groups.map(g => (
                    <div key={g.id} className="bb-member-card" style={{ padding: 0, overflow: 'hidden' }}>
                      <div style={{ background: '#0D9488', height: 4 }} />
                      <div style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#1F2937', marginBottom: 6 }}>{g.name}</div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {g.cohort && <span className="bb-member-badge bb-member-badge--grey" style={{ fontSize: 11 }}>Cohort: {g.cohort}</span>}
                              <span className="bb-member-badge bb-member-badge--grey" style={{ fontSize: 11 }}>{(g.enrolledMembers || []).length} member{(g.enrolledMembers || []).length !== 1 ? 's' : ''}</span>
                              {g.nextSession && <span className="bb-member-badge bb-member-badge--amber" style={{ fontSize: 11 }}>Next: {g.nextSession}</span>}
                            </div>
                          </div>
                          <button
                            className="bb-btn-outline-sm"
                            style={{ fontSize: 11, flexShrink: 0, borderColor: '#0D9488', color: '#0D9488' }}
                            onClick={() => setExpandedGroup(expandedGroup === g.id ? null : g.id)}
                          >
                            {expandedGroup === g.id ? 'Hide ▲' : 'Participants ▼'}
                          </button>
                        </div>
                        {expandedGroup === g.id && (
                          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #E5E7EB' }}>
                            {(g.enrolledMembers || []).length === 0 ? (
                              <span style={{ fontSize: 12, color: '#9CA3AF' }}>No members enrolled yet.</span>
                            ) : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {g.enrolledMembers.map((m, i) => (
                                  <span key={i} style={{ background: '#CCFBF1', borderRadius: 6, padding: '4px 10px', fontSize: 12, color: '#0F766E', fontWeight: 500 }}>{m.memberName}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Mentor: Rebuild & Shine ── */}
          {approvedTypes.includes('Mentor') && (
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #E0E7FF' }}>
                <div style={{ width: 4, height: 22, background: '#4338CA', borderRadius: 2 }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937' }}>🌟 As Mentor</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>Rebuild & Shine Programme</div>
                </div>
              </div>
              <div className="bb-alert bb-alert--info" style={{ marginBottom: 16, fontSize: 12 }}>
                Milestone logs are visible to admin only — they are never shared with members.
              </div>
              {pairs.length === 0 ? (
                <div className="bb-empty-state" style={{ fontSize: 13 }}>No mentoring pairs assigned to you yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {pairs.map(pair => (
                    <div key={pair.id} className="bb-member-card" style={{ padding: 0, overflow: 'hidden' }}>
                      <div style={{ background: '#4338CA', height: 4 }} />
                      <div style={{ padding: '16px 18px' }}>

                        {/* Mentee header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1F2937' }}>
                              {pair.menteeName?.split(' ')[0] || 'Member'}
                            </div>
                            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>
                              Started {pair.startDate?.toDate
                                ? pair.startDate.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                : '—'}
                            </div>
                            {pair.menteeBio && (
                              <p style={{ fontSize: 12, color: '#4B5563', margin: '6px 0 0' }}>{pair.menteeBio}</p>
                            )}
                          </div>
                          <span className={`bb-member-badge ${pair.status === 'active' ? 'bb-member-badge--green' : 'bb-member-badge--grey'}`} style={{ flexShrink: 0 }}>
                            {pair.status === 'active' ? 'Active' : pair.status}
                          </span>
                        </div>

                        {/* Milestone timeline */}
                        {(pair.milestones || []).length > 0 && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                              Logged Milestones
                            </div>
                            <div style={{ borderLeft: '2px solid #C7D2FE', paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                              {pair.milestones.map((m, i) => (
                                <div key={i} style={{ position: 'relative' }}>
                                  <div style={{ position: 'absolute', left: -21, top: 5, width: 8, height: 8, background: '#A5B4FC', borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 0 0 1px #C7D2FE' }} />
                                  <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 500, marginBottom: 2 }}>{fmtMilestoneDate(m.loggedAt)}</div>
                                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{m.note}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Log new milestone */}
                        <div style={{ background: '#F5F7FF', borderRadius: 8, padding: '12px 14px', border: '1px solid #E0E7FF' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#4338CA', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                            Log a Milestone
                          </div>
                          <textarea
                            className="bb-textarea"
                            rows={2}
                            placeholder="Note down a milestone or progress update…"
                            value={milestoneText[pair.id] || ''}
                            onChange={e => setMilestoneText(t => ({ ...t, [pair.id]: e.target.value }))}
                            style={{ fontSize: 13, background: '#fff' }}
                          />
                          {mileSaved[pair.id] && (
                            <div style={{ fontSize: 12, color: '#059669', fontWeight: 500, marginTop: 6 }}>
                              ✓ Milestone logged — visible to admin only.
                            </div>
                          )}
                          <button
                            className="bb-btn-primary"
                            style={{ background: '#4338CA', marginTop: 10, fontSize: 12, padding: '7px 16px' }}
                            disabled={savingMile[pair.id] || !milestoneText[pair.id]?.trim()}
                            onClick={() => handleLogMilestone(pair.id)}
                          >
                            {savingMile[pair.id] ? 'Saving…' : 'Log Milestone'}
                          </button>
                        </div>

                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Befriender: Widow Lifeline ── */}
          {approvedTypes.includes('Befriender') && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #FED7AA' }}>
                <div style={{ width: 4, height: 22, background: '#F97316', borderRadius: 2 }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937' }}>🤲 As Befriender</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>Widow Lifeline Programme</div>
                </div>
              </div>
              {peerConns.length === 0 ? (
                <div className="bb-empty-state" style={{ fontSize: 13 }}>No peer connections assigned to you yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {peerConns.map(c => (
                    <div key={c.id} className="bb-member-card" style={{ padding: 0, overflow: 'hidden' }}>
                      <div style={{ background: '#F97316', height: 4 }} />
                      <div style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#1F2937' }}>{c.memberName?.split(' ')[0] || 'Member'}</div>
                            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                              Started {c.startDate?.toDate
                                ? c.startDate.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                : '—'}
                            </div>
                          </div>
                          <span className={`bb-member-badge ${c.status === 'active' ? 'bb-member-badge--green' : 'bb-member-badge--grey'}`}>
                            {c.status === 'active' ? 'Active' : c.status}
                          </span>
                        </div>
                        {c.status === 'active' && (
                          <PeerChat
                            orgId={orgId}
                            connectionId={c.id}
                            volunteerId={volunteerId}
                            volunteerName={volunteerName}
                            memberFirstName={c.memberName?.split(' ')[0] || 'Member'}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Tab: Appointments & Messages ───────────────────────────────────────────────
function AppointmentsAndMessages({ orgId, volunteerId, volunteerName }) {
  const [appointments, setAppointments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [responding, setResponding] = useState({});
  const [cancelling, setCancelling] = useState({});
  const [openChat, setOpenChat] = useState(null);
  const [msg, setMsg]           = useState('');
  const load = useCallback(() => {
    Promise.all([
      getVolunteerAppointments(orgId, volunteerId),
      getNotifications(orgId, volunteerId),
    ]).then(([appts, notifs]) => {
      setAppointments(appts);
      setNotifications(notifs);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [orgId, volunteerId]);

  useEffect(() => { load(); }, [load]);

  // Re-check every 60 s so the LIVE badge and chat window auto-appear at appointment time
  useEffect(() => {
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const markRead = async (notifId) => {
    await markNotificationRead(orgId, notifId).catch(() => {});
    setNotifications(ns => ns.map(n => n.id === notifId ? { ...n, read: true } : n));
  };

  const handleRespond = async (appointmentId, approved) => {
    setResponding(r => ({ ...r, [appointmentId]: true }));
    try {
      await respondToAppointment(orgId, appointmentId, approved);
      setMsg(approved ? 'Appointment confirmed. The member has been notified.' : 'Appointment declined. The time slot is now available again.');
      load();
    } catch (err) {
      setMsg(err.message || 'Error responding to appointment.');
    } finally { setResponding(r => ({ ...r, [appointmentId]: false })); }
  };

  const handleCancel = async (appointmentId) => {
    if (!window.confirm('Cancel this confirmed appointment? The member will need to rebook.')) return;
    setCancelling(c => ({ ...c, [appointmentId]: true }));
    try {
      await cancelAppointment(orgId, appointmentId, volunteerId);
      setMsg('Appointment cancelled.');
      load();
    } catch { setMsg('Error cancelling appointment.'); }
    finally { setCancelling(c => ({ ...c, [appointmentId]: false })); }
  };

  const pending   = appointments.filter(a => a.status === 'pending_approval');
  const confirmed = appointments.filter(a => a.status === 'confirmed');
  const past      = appointments.filter(a => ['declined', 'cancelled', 'completed'].includes(a.status));
  const unread    = notifications.filter(n => !n.read);

  if (loading) return (
    <div className="bb-vol-section">
      <div className="bb-loading-row"><div className="bb-loading-spinner" /><span>Loading appointments…</span></div>
    </div>
  );

  return (
    <div className="bb-vol-section">
      {msg && <div className="bb-alert bb-alert--info" style={{ marginBottom: 12 }}>{msg}</div>}

      {/* Notifications banner */}
      {unread.length > 0 && (
        <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#92400E', marginBottom: 6 }}>
            🔔 {unread.length} new notification{unread.length > 1 ? 's' : ''}
          </div>
          {unread.map(n => (
            <div key={n.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#78350F', marginBottom: 4 }}>
              <span>{n.message}</span>
              <button className="bb-btn-outline-sm" style={{ fontSize: 10, marginLeft: 8 }} onClick={() => markRead(n.id)}>Dismiss</button>
            </div>
          ))}
        </div>
      )}

      {/* Pending requests */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1F2937', marginBottom: 10 }}>
          Appointment Requests
          {pending.length > 0 && (
            <span style={{ background: '#EF4444', color: '#fff', borderRadius: 12, fontSize: 11, padding: '1px 7px', marginLeft: 8 }}>{pending.length}</span>
          )}
        </h3>

        {pending.length === 0 ? (
          <div className="bb-empty-state" style={{ fontSize: 13 }}>No pending requests.</div>
        ) : (
          pending.map(appt => (
            <div key={appt.id} className="bb-card" style={{ marginBottom: 12, borderLeft: '3px solid #F59E0B' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1F2937' }}>
                    {appt.memberName?.split(' ')[0] || 'A member'} requests an appointment
                  </div>
                  <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
                    {fmtDate(appt.date)} · {fmt12(appt.time)} · {appt.duration} min
                  </div>
                </div>
                <span className="bb-badge bb-badge--yellow">Awaiting Response</span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                  className="bb-btn-primary"
                  style={{ background: '#16A34A', fontSize: 12, padding: '6px 14px' }}
                  disabled={responding[appt.id]}
                  onClick={() => handleRespond(appt.id, true)}
                >
                  {responding[appt.id] ? '…' : '✓ Approve'}
                </button>
                <button
                  className="bb-btn-danger-sm"
                  disabled={responding[appt.id]}
                  onClick={() => handleRespond(appt.id, false)}
                >
                  {responding[appt.id] ? '…' : '✗ Decline'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Confirmed appointments */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1F2937', marginBottom: 10 }}>Upcoming Appointments</h3>
        {confirmed.length === 0 ? (
          <div className="bb-empty-state" style={{ fontSize: 13 }}>No confirmed appointments.</div>
        ) : (
          confirmed.map(appt => {
            const active = isMessagingOpen(appt);
            const chatOpen = openChat === appt.id;
            return (
              <div key={appt.id} className="bb-card" style={{ marginBottom: 12, borderLeft: `3px solid ${active ? '#4338CA' : '#D1D5DB'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1F2937' }}>
                      {appt.memberName?.split(' ')[0] || 'Member'}
                    </div>
                    <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
                      {fmtDate(appt.date)} · {fmt12(appt.time)} · {appt.duration} min
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span className="bb-badge bb-badge--green">Confirmed</span>
                    {active && (
                      <span style={{ background: '#4338CA', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8 }}>
                        LIVE
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  {active ? (
                    <button
                      className="bb-btn-primary"
                      style={{ background: '#4338CA', fontSize: 12, padding: '6px 14px' }}
                      onClick={() => setOpenChat(chatOpen ? null : appt.id)}
                    >
                      💬 {chatOpen ? 'Close Chat' : 'Open Chat'}
                    </button>
                  ) : (
                    <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
                      Chat opens at appointment time
                    </span>
                  )}
                  <button
                    className="bb-btn-outline-sm"
                    style={{ fontSize: 11, color: '#DC2626', borderColor: '#DC2626' }}
                    disabled={cancelling[appt.id]}
                    onClick={() => handleCancel(appt.id)}
                  >
                    {cancelling[appt.id] ? '…' : 'Cancel'}
                  </button>
                </div>

                {chatOpen && active && (
                  <AppointmentChat
                    orgId={orgId}
                    appointmentId={appt.id}
                    volunteerId={volunteerId}
                    volunteerName={volunteerName}
                    memberFirstName={appt.memberName?.split(' ')[0] || 'Member'}
                  />
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Past appointments */}
      {past.length > 0 && (
        <details style={{ fontSize: 12, color: '#6B7280' }}>
          <summary style={{ cursor: 'pointer', marginBottom: 8, fontWeight: 600, fontSize: 13, color: '#374151' }}>
            Past &amp; Declined Appointments ({past.length})
          </summary>
          <table className="bb-table">
            <thead><tr><th>Member</th><th>Date</th><th>Time</th><th>Duration</th><th>Status</th></tr></thead>
            <tbody>
              {past.map(a => {
                const badge = APPT_STATUS[a.status] || { cls: 'bb-badge--gray', label: a.status };
                return (
                  <tr key={a.id} style={{ opacity: 0.7 }}>
                    <td>{a.memberName?.split(' ')[0] || '—'}</td>
                    <td>{fmtDate(a.date)}</td>
                    <td>{fmt12(a.time)}</td>
                    <td>{a.duration} min</td>
                    <td><span className={`bb-badge ${badge.cls}`}>{badge.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
function SupportNetworkVolunteer({ orgId, volunteerId, volunteerName, activeTab }) {
  return (
    <div className="bb-vol-tool">
      <div className="bb-vol-tool-header">
        <h2>Support Network</h2>
      </div>

      {activeTab === 'View Available Roles' && (
        <ViewAvailableRoles orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} />
      )}
      {activeTab === 'My Roles, Groups & Pairs' && (
        <MyRolesGroupsAndPairs orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} />
      )}
      {activeTab === 'My Messages' && (
        <AppointmentsAndMessages orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} />
      )}

      <FlagConcernPanel orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} />
    </div>
  );
}

export default SupportNetworkVolunteer;
